import { useCallback, useSyncExternalStore } from 'react';

export type ThemeChoice = 'system' | 'light' | 'dark';
const KEY = 'mydash.theme';
const listeners = new Set<() => void>();

function read(): ThemeChoice {
  try {
    const v = localStorage.getItem(KEY);
    return v === 'light' || v === 'dark' ? v : 'system';
  } catch {
    return 'system';
  }
}

export function applyTheme(choice: ThemeChoice): void {
  const root = document.documentElement;
  if (choice === 'system') root.removeAttribute('data-theme');
  else root.setAttribute('data-theme', choice);
  try {
    if (choice === 'system') localStorage.removeItem(KEY);
    else localStorage.setItem(KEY, choice);
  } catch {
    /* ignore */
  }
  for (const l of listeners) l();
}

function subscribe(l: () => void) {
  listeners.add(l);
  const mq = window.matchMedia?.('(prefers-color-scheme: dark)');
  mq?.addEventListener?.('change', l);
  return () => {
    listeners.delete(l);
    mq?.removeEventListener?.('change', l);
  };
}

/** The saved choice and whether the page is currently dark. */
export function useTheme() {
  const choice = useSyncExternalStore(subscribe, read, () => 'system' as ThemeChoice);
  const isDark = useSyncExternalStore(
    subscribe,
    () => choice === 'dark' || (choice === 'system' && !!window.matchMedia?.('(prefers-color-scheme: dark)').matches),
    () => false,
  );
  const setTheme = useCallback((c: ThemeChoice) => applyTheme(c), []);
  return { choice, isDark, setTheme };
}
